import { $, pageData } from '../lib/dom.js';
import { ct } from '../lib/i18n.js';
import { api } from '../lib/api.js';
import { fillForm } from '../lib/form.js';
import { trackDirtyForm } from '../lib/dirty-form.js';
import { findSelectMenu } from '../lib/select-menu.js';

/**
 * The Counting page.
 *
 * One configuration, one save bar. The live figures are drawn outside the form
 * and are never read here: `current`, `highScore` and `totalCounted` are the
 * state of a game in progress, and the API refuses them anyway.
 *
 * The reserve of saves is the exception, and it is a setting: the server grants
 * them, the game spends them.
 */

interface Settings {
  channelId: string;
  failRoleId: string;
  failRoleMinutes: number;
  saves: number;
  allowMaths: boolean;
  numbersOnly: boolean;
  neverBreak: boolean;
  reactOnCount: boolean;
}

export function initCountingPage(): void {
  const form = $<HTMLFormElement>('[data-counting-form]');
  if (!form) return;

  const { settings, guildId } = pageData<{ settings: Settings; guildId: string }>();

  /*
   * The save bar first, before any widget is mounted: saving is this page's
   * essential function and must not depend on a select that can throw
   * (AGENTS §3.5).
   */
  trackDirtyForm({
    form,
    successMessage: ct('settingsSaved', 'Settings saved.'),
    onSave: async () => {
      await api.put(`/api/counting?guildId=${guildId}`, {
        guildId,
        channelId: findSelectMenu(form, 'channelId')?.single ?? '',
        failRoleId: findSelectMenu(form, 'failRoleId')?.single ?? '',
        failRoleMinutes: number(form, 'failRoleMinutes'),
        saves: number(form, 'saves'),
        allowMaths: checkbox(form, 'allowMaths'),
        numbersOnly: checkbox(form, 'numbersOnly'),
        neverBreak: checkbox(form, 'neverBreak'),
        reactOnCount: checkbox(form, 'reactOnCount'),
      });
    },
  });

  fillForm(form, settings as unknown as Record<string, unknown>);

  const channel = findSelectMenu(form, 'channelId');
  if (channel) channel.value = settings.channelId ? [settings.channelId] : [];

  const role = findSelectMenu(form, 'failRoleId');
  if (role) role.value = settings.failRoleId ? [settings.failRoleId] : [];
}

function number(form: HTMLFormElement, name: string): number {
  const field = form.elements.namedItem(name);
  return field instanceof HTMLInputElement ? Number(field.value) || 0 : 0;
}

function checkbox(form: HTMLFormElement, name: string): boolean {
  const field = form.elements.namedItem(name);
  return field instanceof HTMLInputElement ? field.checked : false;
}
